import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import type { HeadersFunction, LoaderFunction, MetaFunction } from "@remix-run/cloudflare";
import { json, redirect } from "@remix-run/cloudflare";
import { Link, useLoaderData, useNavigate } from "@remix-run/react";
import { useState } from "react";
import invariant from "tiny-invariant";
import Carousel from "~/components/carousel.component";
import Neum from "~/components/neum.component";
import { Categories } from "~/components/product/categories.component";
import { Gallery } from "~/components/product/gallery.component";
import ProductCarousel from "~/components/product/productCarousel.component";
import ProductReviews from "~/components/product/productReviews";
import PurchaseGrid from "~/components/product/purchaseGrid.component";
import { MIN_QUANTITY_AVAILABLE, expiresTenDays } from "~/constants";
import { admirer_cart } from "~/cookies";
import useScript from "~/hooks/useScript";
import CartGQL from "~/shopify/API/cart.server";
import { getCollectionProducts } from "~/shopify/API/collection-products.server";
import { getProduct } from "~/shopify/API/product.server";
import { getRecentProducts } from "~/shopify/API/recent-products.server";
import type { ProductVariant } from "~/shopify/types/gql/graphql";
import type { Option, ProductFlatten } from "~/types";

type LoaderData = { product: ProductFlatten };

export const loader: LoaderFunction = async ({ params }): Promise<LoaderData> => {
  invariant(params.product, "expected params.product");
  // TODO handle error in product api, product is null here
  const { product, error } = await getProduct(params.product);
  console.error(error);
  invariant(product, "expected product not null");

  return { product };
};

export const meta: MetaFunction = ({ data }) => {
  const { product } = data as LoaderData;

  const collectionTitles = product.collections?.map((collection) => collection.title).join(", ") || "";

  return [
    { title: `Admirer Store - ${product.title}` },
    { name: "description", content: product.description },
    { name: "robots", content: "index,follow" },
    collectionTitles && { name: "keywords", content: collectionTitles },
    { property: "og:title", content: product.title },
    { property: "og:description", content: product.description },
    product.images?.[0]?.url && {
      property: "og:image",
      content: `${product.images[0].url}&width=500`,
    },
  ];
};

export const headers: HeadersFunction = () => {
  // data cached 10 minutes in the browser, one hour(3600) in the server-cdn, stale while revalidate for 24 hours(86400)
  return {
    "cache-control": "public, max-age=600, s-maxage=3600, stale-while-revalidate=86400",
  };
};

const createOptionsObject = (options: Option[], variants: ProductVariant[]): { [key: string]: string } => {
  const initialOptions: { [key: string]: string } = {};
  if (variants.length === 1) {
    for (const option of options) {
      initialOptions[option.name] = option.values[0];
    }
  } else {
    for (const option of options) {
      initialOptions[option.name] = "";
    }
  }

  return initialOptions;
};

export async function action({ request }: { request: Request }) {
  const formData = await request.formData();
  const variantId = (formData.get("variant") as string) ?? "";

  const cookieHeader = request.headers.get("Cookie");
  let cartId = ((await admirer_cart.parse(cookieHeader)) as string) ?? "";
  const cartResult = cartId ? await CartGQL.addToCart(cartId, variantId) : await CartGQL.createCart(variantId);
  const { cart, error } = cartResult;

  if (cart) {
    cartId = cart.id;

    return redirect("/cart", {
      headers: {
        // set cookie and update expires date to current date + 10 days
        "Set-Cookie": await admirer_cart.serialize(cartId, {
          expires: expiresTenDays(),
        }),
      },
    });
  }
  // handle error in cart api where we need to create new cart and delete old one
  // trying to add variant again but to a new cart
  if (
    error?.message.includes("expected cart in response") ||
    error?.message.includes("The specified cart does not exist")
  ) {
    const newCartResult = await CartGQL.createCart(variantId);
    if (newCartResult.cart) {
      cartId = newCartResult.cart.id;
      return redirect("/cart", {
        headers: {
          // set cookie and update expires date to current date + 10 days
          "Set-Cookie": await admirer_cart.serialize(cartId, {
            expires: expiresTenDays(),
          }),
        },
      });
    }
    return json(
      // TODO show alert to user to try again
      { error: newCartResult.error?.message },
    );
  }
  return json(
    // TODO show alert to user to try again
    { error: error?.message },
  );
}

export default function ProductPage() {
  // TODO do not use as LoaderData, handle serialize/desearilize corectly
  const { product } = useLoaderData() as LoaderData;
  const [selectedOptions, setSelectedOptions] = useState(createOptionsObject(product.options, product.variants));
  const [optionsQueue, setOptionsQueue] = useState<string[]>([]);
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { id, title, collections, media, descriptionHtml } = product;

  useScript(
    "gtag_add_to_cart",
    `
      window.dataLayer = window.dataLayer || [];
            dataLayer.push({
              event: "add_to_cart",
              items:[
                {
                  item_id: "${id}",
                  item_name: "${title}",
                  item_category: "${collections[0].handle}",
                }
              ]
            });
    `,
  );

  return (
    <div className="px-4">
      <main className="pt-4 sm:pt-8">
        <div className="flex items-center justify-between gap-2.5">
          <div className="block flex-shrink-0 lg:hidden">
            <Link onClick={goBack} to={""} className="text-admirerBlue">
              <ChevronLeftIcon className="float-left h-6 w-6" aria-hidden="true" /> Back
            </Link>
          </div>
          <div className="flex-grow">
            <Categories product={product} />
          </div>
        </div>

        <h1 className="pt-2 text-lg font-extrabold text-admirerBlack-900 dark:text-white sm:hidden sm:font-bold md:text-xl">
          {title}
        </h1>

        <div className="mx-auto max-w-2xl pt-3 md:max-w-3xl md:pt-10 lg:grid lg:max-w-7xl lg:grid-cols-8 lg:grid-rows-[auto,auto,1fr] lg:gap-x-8 lg:px-0 lg:pt-5">
          <div className="lg:col-span-4 lg:pr-3">
            <ProductCarousel
              product={product}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              optionsQueue={optionsQueue}
              setOptionsQueue={setOptionsQueue}
            />
            <div className="right-0 top-0 col-span-3 row-span-2 mt-2 block w-full md:col-span-4 md:mt-4 md:h-fit lg:col-end-9 lg:mt-0 lg:hidden">
              <PurchaseGrid
                product={product}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                optionsQueue={optionsQueue}
                setOptionsQueue={setOptionsQueue}
                isDesktop={false}
              />
            </div>
            {/* Description and details */}
            <div>
              <h3 className="sr-only">Description</h3>
            </div>
            <div
              className="product-info md:mt-5"
              dangerouslySetInnerHTML={{
                __html: descriptionHtml as string,
              }}
            />
            {/* Video */}
            <div className="grid auto-rows-max grid-cols-12 gap-3 py-3">
              {media
                .filter((item) => Object.keys(item).length > 0)
                .map((item) => (
                  <Neum className="col-span-12 w-full p-1" key={item.id}>
                    {Array.isArray(item.sources) && item.sources.length > 0 && (
                      <div>
                        <video width="100%" height="400" controls>
                          <source src={(item.sources[0] as { url: string }).url} type="video/mp4" />
                          <track src="" kind="captions" label="English" default />
                        </video>
                      </div>
                    )}
                  </Neum>
                ))}
            </div>
            {/* Imagenes */}
            <Gallery product={product} />
          </div>
          <div className="sticky right-0 top-0 col-span-3 row-span-2 mt-2 hidden w-full md:col-span-4 md:mt-4 md:h-fit lg:col-end-9 lg:mt-0 lg:block">
            <PurchaseGrid
              product={product}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              optionsQueue={optionsQueue}
              setOptionsQueue={setOptionsQueue}
              isDesktop={true}
            />
          </div>
        </div>

        {/* Product reviews */}
        <div className="mt-8">
          <ProductReviews productHandle={product.handle} />
        </div>

        <div className="pb-1 pt-0 text-white md:py-10 lg:col-span-8  lg:col-start-1 lg:pb-8 lg:pr-8 lg:pt-6">
          <Carousel
            category={collections[0].handle}
            title="Our Favorites"
            link={collections[0] ? `/category/${collections[0].handle}` : "/all"}
          />
        </div>
      </main>
    </div>
  );
}
